/* eslint-disable react/jsx-props-no-spreading */
import { useEffect, FunctionComponent } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { PageTransition } from 'next-page-transitions'
import publicConfig from '@/config/public'
import { initAnalytics, trackRouteView } from '@/libs/helpers/analytics'
import { isPageBlockingError } from '@/libs/helpers/error_handler'
import type { ServerSideProps } from '@/libs/helpers/withServerSidePropsDecorator'
import Providers from '@/providers'
import ErrorPage from './_error'

const InitializeObservability: FunctionComponent<{
  children: JSX.Element
}> = ({ children }) => {
  const router = useRouter()

  useEffect(() => {
    initAnalytics()
    // The GTM container fired view_menu/view_checkout/order_placed off pageview
    // and history-change triggers; this is the same coverage for a Next.js SPA.
    trackRouteView(router.asPath)
    router.events.on('routeChangeComplete', trackRouteView)
    return () => router.events.off('routeChangeComplete', trackRouteView)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      datadogRum.init({
        applicationId: publicConfig.datadogApplicationId,
        clientToken: publicConfig.datadogClientToken,
        service: publicConfig.datadogService,
        env: publicConfig.environment,
        site: 'datadoghq.com',
        trackResources: true,
        trackUserInteractions: false, // does not leak PII to DD RUM when false
        sessionSampleRate: 100,
        sessionReplaySampleRate: 1,
        useCrossSiteSessionCookie: true,
        version: process.env.NEXT_PUBLIC_GITSHA,
        allowedTracingUrls: [/https:\/\/.*\.tbdine\.com/],
        defaultPrivacyLevel: 'allow',
        // The RUM SDK forwards CSP violation reports as RUM errors, double-counting
        // the Report-Only CSP telemetry (its own stream: source:csp-report) and
        // polluting error monitors. Drop them here; everything else passes through.
        beforeSend: (event) => !(event.type === 'error' && event.error.source === 'report'),
      })
      datadogRum.startSessionReplayRecording()
    }
  }, [])

  return children
}

const MyApp: FunctionComponent<AppProps<ServerSideProps>> = ({
  Component: PageComponent,
  pageProps,
  router,
}) => {
  return (
    <InitializeObservability>
      <>
        <Head>
          <title>TouchBistro - Online Ordering</title>
          <meta
            // https://developer.mozilla.org/en-US/docs/Web/HTML/Viewport_meta_tag#viewport_basics
            content="width=device-width,minimum-scale=1,maximum-scale=1,initial-scale=1"
            name="viewport"
            key="viewport"
          />
        </Head>
        {/* Google Analytics 4, loaded directly — the GTM container is gone
            (APPSEC-64; event parity spec in the hacks repo, gtag-swap/). This
            /gtag/js path is exactly the CSP script-src allowance; gtag config
            and all event calls live in src/libs/helpers/analytics.ts. */}
        {Boolean(publicConfig.ga4MeasurementId) && (
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${publicConfig.ga4MeasurementId}`}
            strategy="afterInteractive"
          />
        )}
        <Providers pageProps={pageProps}>
          <PageTransition timeout={250} classNames="page-transition">
            {pageProps.error != null && isPageBlockingError(pageProps.error.statusCode) ? (
              <ErrorPage serverError={pageProps.error} />
            ) : (
              <PageComponent {...pageProps} key={router.route} />
            )}
          </PageTransition>
        </Providers>
      </>
    </InitializeObservability>
  )
}

export default MyApp
